import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import type { ErrorLike } from '@apollo/client'

import { IconButton, SendHorizontalIcon } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'

import { SummaryMode } from '../../components/MetricChart'
import type { MetricSeries } from '../../components/MetricChart'
import { NoDataEmptyState } from '../../components/NoDataEmptyState'
import { useExportChartAsPost } from '../../hooks/useExportChartAsPost'
import { captureChartAsBlob } from '../../chartToImage'
import { useRegisterSnapshots } from '../../context'
import { formatDateRange, getNoDataDescription } from '../../utils'
import type { ChannelTimeSeries } from '../hooks/useAllChannelsTimeSeries'

import { PostsChartSectionView } from './ChartSectionViews'
import type { PostsView } from './ChartViewToggle'
import { toPostsBarSeries } from './toPostsBarSeries'
import { toPostsStackedBarSeries } from './toPostsStackedBarSeries'
import { useMetricChannels } from './useMetricChannels'
import sectionButtonStyles from '../../components/sectionActionButton.module.css'

interface AllChannelsPostsChartProps {
  channelTimeSeries: ChannelTimeSeries[]
  totalChannelCount: number
  loading: boolean
  refreshing?: boolean
  error?: ErrorLike
  startDate: Date
  endDate: Date
  selectedDateRange?: string
  hasChannelFilter?: boolean
  onClearChannelFilter?: () => void
  /** When false, hides the "Share as Post" button (sharing opens the composer). */
  canCreatePosts?: boolean
}

export function AllChannelsPostsChart({
  channelTimeSeries,
  totalChannelCount,
  loading,
  refreshing = false,
  error,
  startDate,
  endDate,
  selectedDateRange,
  hasChannelFilter,
  onClearChannelFilter,
  canCreatePosts = true,
}: AllChannelsPostsChartProps): JSX.Element {
  const [postsView, setPostsView] = useState<PostsView>('bar')

  const metricChannels = useMetricChannels({
    channelTimeSeries,
    metric: 'posts',
    totalChannelCount,
    hasChannelFilter,
  })
  const { displayedChannels, channels, paletteIndexById } = metricChannels

  // One bar series per channel — per-day post counts. Feeds the Stacked Bar
  // (over-time) view. Colours come from the shared hook so a channel matches
  // its Followers-card colour.
  const barSeries = useMemo(
    () => toPostsStackedBarSeries(displayedChannels, paletteIndexById),
    [displayedChannels, paletteIndexById],
  )

  const postsBarSeries = useMemo(
    () => toPostsBarSeries(displayedChannels, paletteIndexById),
    [displayedChannels, paletteIndexById],
  )

  // Same series adapted for the Share-as-Post snapshot (Total per-day counts).
  const chartMetrics = useMemo<MetricSeries[]>(
    () =>
      barSeries.map((series) => ({
        key: series.key,
        title: series.label,
        icon: series.icon,
        color: series.color,
        summaryMode: SummaryMode.Total,
        data: series.data,
      })),
    [barSeries],
  )

  const exportText = useMemo(
    () =>
      channels.length === 1
        ? `Check out how I've been posting on ${channels[0].name}! 📊`
        : `Check out how I've been posting across my channels! 📊`,
    [channels],
  )

  const { account } = useAccount()

  const visibleChannelCount = channels.length
  const channelCountLabel = `${visibleChannelCount} ${
    visibleChannelCount === 1 ? 'channel' : 'channels'
  }`

  const stacked = postsView === 'stackedBar'

  // Bar view snapshots the ranked horizontal chart; Stacked Bar snapshots the
  // over-time stacked bars (chartMetrics + chartStacked).
  const rankedBars = { series: postsBarSeries, xAxisLabel: 'Posts sent' }

  const snapshotBase = {
    channelIds: displayedChannels.map((ch) => ch.channel.id),
    text: exportText,
    heading: 'Posts',
    subheading: `${channelCountLabel} · ${formatDateRange(startDate, endDate)}`,
    channelAvatarUrl: account?.avatar ?? undefined,
    altText: `Buffer Insights chart of Posts across ${channelCountLabel} over ${formatDateRange(startDate, endDate)}.`,
    chartMetrics,
    chartStartDate: startDate,
    chartEndDate: endDate,
    chartSingleAxis: true,
    chartYAxisLabel: 'Posts',
  }

  // "Share as Post" keeps the currently-selected view.
  const chartCaptureOptions = {
    ...snapshotBase,
    rankedBars: stacked ? undefined : rankedBars,
    chartStacked: stacked,
  }

  const { onExport, isExporting } = useExportChartAsPost(chartCaptureOptions)

  // A channel with only zero-valued days has no posts to chart, so a set of
  // all-zero series is treated as "no data": show the empty state instead of a
  // chart of empty bars. @see CORE-6428
  const hasMeaningfulData = barSeries.some((series) =>
    series.data.some((point) => point.value != null && point.value !== 0),
  )

  const snapshotsAvailable =
    hasMeaningfulData && !loading && !error && !refreshing
  useRegisterSnapshots([
    {
      id: 'posts-bar',
      filename: 'posts-bar.png',
      order: 20,
      available: snapshotsAvailable,
      getBlob: async (): Promise<Blob> =>
        (
          await captureChartAsBlob({
            ...snapshotBase,
            rankedBars,
            chartStacked: false,
          })
        ).blob,
    },
    {
      id: 'posts-stacked-bar',
      filename: 'posts-stacked-bar.png',
      order: 21,
      available: snapshotsAvailable,
      getBlob: async (): Promise<Blob> =>
        (
          await captureChartAsBlob({
            ...snapshotBase,
            rankedBars: undefined,
            chartStacked: true,
          })
        ).blob,
    },
  ])

  return (
    <PostsChartSectionView
      metricChannels={metricChannels}
      totalChannelCount={totalChannelCount}
      stackedSeries={barSeries}
      barSeries={postsBarSeries}
      view={postsView}
      onViewChange={setPostsView}
      startDate={startDate}
      endDate={endDate}
      dateLabel={formatDateRange(startDate, endDate)}
      loading={loading}
      error={error}
      refreshing={refreshing}
      emptyState={
        <NoDataEmptyState
          description={getNoDataDescription(
            selectedDateRange,
            hasChannelFilter,
          )}
          onClearChannelFilter={onClearChannelFilter}
          size="small"
        />
      }
      headerExtraActions={
        canCreatePosts && (
          <IconButton
            className={sectionButtonStyles.neutralBackground}
            size="medium"
            onClick={onExport}
            loading={isExporting}
            label={isExporting ? 'Sharing Posts…' : 'Share as Post for Posts'}
            tooltip={isExporting ? 'Sharing…' : 'Share as Post'}
          >
            <SendHorizontalIcon />
          </IconButton>
        )
      }
    />
  )
}
